
import { jitsiLocalStorage } from '@jitsi/js-utils';

const AVATAR_COLORS = [
];

/**
 * Generates the background color of an initials based avatar.
 *
 * @param {string?} initials - The initials of the avatar.
 * @returns {string}
 */
export function getAvatarColor(initials: ?string) {
    let colorIndex = 0;


    // If initials are not provided, we use a default color
    if (!initials) {
        return `rgba(${AVATAR_COLORS[colorIndex]}`;
    }

    if (initials) {
        let nameHash = 0;

        for (const s of initials) {
            nameHash += s.codePointAt(0);
        }

        colorIndex = nameHash % AVATAR_COLORS.length;
    }

    return `rgba(${AVATAR_COLORS[colorIndex]}`;
}

/**
 * Generates initials for a simple string.
 *
 * @param {string?} s - The string to generate initials for.
 * @returns {string?}
 */
export function getInitials(s: ?string) {
    // We don't want to use the domain part of an email address, if it is one
    const initialsBasis = s ? s.split('@')[0] : '';

    // Extract words using regex - matches sequences of alphanumeric characters
    const wordsArray = initialsBasis.match(/[a-zA-Z0-9]+/g) || [];
    let initials = '';

    for (const w of wordsArray) {
        initials.length < 2 && (initials += w.substr(0, 1).toUpperCase());
    }

    return initials;
}

const participantColors = new Map(); // Track participant ID to color mapping
const dynamicAvatarColors = [ ...AVATAR_COLORS ]; // Dynamic array that can grow
const usedColorIndices = new Set(); // Track used color indices

/**
 * Colors of signed in people, remembered across page loads so that reloading a
 * tab or reconnecting does not repaint the meeting. Keyed by the stable person
 * id, never by an endpoint id, which is why only identity-keyed assignments ask
 * for persistence.
 */
const COLOR_STORAGE_KEY = 'meethour.participant-colors';
const MAX_PERSISTED_COLORS = 60;
let persistedColors;

/**
 * Reads the remembered colors, once per page load.
 *
 * @returns {Object}
 */
function _getPersistedColors() {
    if (!persistedColors) {
        persistedColors = {};

        try {
            const stored = JSON.parse(jitsiLocalStorage.getItem(COLOR_STORAGE_KEY) || '{}');

            if (stored && typeof stored === 'object') {
                persistedColors = stored;
            }
        } catch {
            // Corrupt or unavailable storage just means we pick fresh colors.
        }
    }

    return persistedColors;
}

/**
 * Remembers the color of a person and reserves it so that nobody else in this
 * meeting is given the same one.
 *
 * @param {string} key - The stable person id.
 * @param {string} color - The color to remember.
 * @returns {void}
 */
function _persistColor(key, color) {
    const colors = _getPersistedColors();

    if (colors[key] === color) {
        return;
    }

    colors[key] = color;

    const keys = Object.keys(colors);

    for (const stale of keys.slice(0, Math.max(0, keys.length - MAX_PERSISTED_COLORS))) {
        delete colors[stale];
    }

    try {
        jitsiLocalStorage.setItem(COLOR_STORAGE_KEY, JSON.stringify(colors));
    } catch {
        // Not being able to remember the color is not worth failing a join for.
    }
}

/**
 * Marks a color as taken so the generator does not hand it to someone else.
 *
 * @param {string} color - The color to reserve.
 * @returns {void}
 */
function _reserveColor(color) {
    let index = dynamicAvatarColors.indexOf(color);

    if (index === -1) {
        index = dynamicAvatarColors.length;
        dynamicAvatarColors.push(color);
    }

    usedColorIndices.add(index);
}

/**
 * Generates a new random color in HSL format.
 *
 * @returns {string}
 */
function generateRandomColor() {
    const randomHue = Math.floor(Math.random() * 360);
    const hue = (randomHue + 137.508) % 360;
    const finalHue = Math.round(hue);
    const saturation = 39;
    const lightness = 30;


    return `hsl(${finalHue}, ${saturation}%, ${lightness}%)`;
}

/**
 * Gets the next available color, generating a new one if needed.
 *
 * @returns {string}
 */
function getNextAvailableColor() {
    // First, try to use existing colors from the original AVATAR_COLORS
    for (let i = 0; i < dynamicAvatarColors.length; i++) {
        if (!usedColorIndices.has(i)) {
            usedColorIndices.add(i);

            // Convert original AVATAR_COLORS format to rgba, keep HSL as is
            if (dynamicAvatarColors[i].includes(',') && !dynamicAvatarColors[i].includes('hsl')) {
                return `rgba(${dynamicAvatarColors[i]}, 0.4)`;
            }

            return dynamicAvatarColors[i];
        }
    }

    // If all existing colors are used, generate a new unique color
    const newColor = generateRandomColor();
    const newIndex = dynamicAvatarColors.length;

    dynamicAvatarColors.push(newColor);
    usedColorIndices.add(newIndex);

    return newColor;
}

/**
 * Generates a unique color for participants.
 *
 * @returns {string}
 */
export function getUniqueParticipantColor() {
    return getNextAvailableColor();
}

/**
 * Assigns a color to a participant if they don't already have one.
 *
 * @param {string} id - The key to assign a color to. This is the stable person
 * id when the participant is signed in, and the endpoint id otherwise.
 * @param {Object} participant - The participant object.
 * @param {Object} [options] - Set {@code persist} when {@code id} identifies a
 * person rather than an endpoint, so the color survives a page reload.
 * @returns {string|null} - The assigned color or null if participant already has a color.
 */
export function assignColorToParticipant(id, participant, options = {}) {
    // const interfaceConfig = APP.store.getState()['features/base/config'].interfaceConfig;
    // const tileBackground = getBrandingColorPalette(interfaceConfig)?.tileBackground;

    // console.log(tileBackground, 'tileBackground assignColorToParticipant');
    const existingColor = participant?.color;

    // if (tileBackground !== 'multiple') {
    //     participantColors.set(id, tileBackground);

    //     return null;
    // }

    // If participant already has a color, don't assign a new one
    if (existingColor !== undefined) {
        // Update our tracking
        participantColors.set(id, existingColor);

        return null;
    }

    // Check if we already assigned a color to this participant
    if (participantColors.has(id)) {
        return participantColors.get(id);
    }

    // Give a signed in person the color they had before this page load, so a
    // reload or a reconnect does not repaint them.
    const rememberedColor = options.persist ? _getPersistedColors()[id] : undefined;
    const newColor = rememberedColor || getUniqueParticipantColor();

    if (rememberedColor) {
        _reserveColor(rememberedColor);
    }

    participantColors.set(id, newColor);

    if (options.persist) {
        _persistColor(id, newColor);
    }

    return newColor;
}

/**
 * Removes a participant's color from tracking when they leave.
 *
 * @param {string} id - The participant ID to remove.
 */
export function removeParticipantColor(id) {
    const color = participantColors.get(id);

    if (color) {
        participantColors.delete(id);

        // Persisted person colors remain reserved for this page. Releasing one
        // while its owner is away lets another participant take it, producing a
        // collision when the original person returns.
        if (_getPersistedColors()[id] === color) {
            return;
        }

        // Find the color index and mark it as available for reuse
        const colorIndex = dynamicAvatarColors.findIndex(c => {
            // Handle original AVATAR_COLORS format (converted to rgba)
            if (c.includes(',') && !c.includes('hsl') && color.includes('rgba')) {
                return `rgba(${c}, 0.4)` === color;
            }


            // Handle HSL colors directly
            return c === color;
        });

        if (colorIndex !== -1) {
            usedColorIndices.delete(colorIndex);
        }
    }
}

/**
 * Gets the assigned color for a participant.
 *
 * @param {string} id - The participant ID.
 * @returns {string|undefined} - The assigned color or undefined if not found.
 */
export function getParticipantColor(id) {
    return participantColors.get(id);
}

/**
 * Gets information about the current color system state (for debugging).
 *
 * @returns {Object} - Color system information.
 */
export function getColorSystemInfo() {
    return {
        totalColors: dynamicAvatarColors.length,
        usedColors: usedColorIndices.size,
        availableColors: dynamicAvatarColors.length - usedColorIndices.size,
        participantCount: participantColors.size,
        colors: [ ...dynamicAvatarColors ],
        usedIndices: [ ...usedColorIndices ]
    };
}
